import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { awaitSessionBootstrap } from '../../shell/session/hydrateSessionCachesFromBootstrap';
import { resolveSessionEmployee, type SessionEmployeeRecord } from '../application/sessionEmployeeResolver';
import { subscribeEmployeePermissionsFreshness } from '../../shell/session/employeePermissionsFreshnessEvents';
import { isDatabaseUnavailableError } from '../../shared/localDb';

export type SessionEmployeeRecordState = {
  /** 'loading' = resolviendo; 'ready' = resuelto; 'error' = bootstrap/resolver throweó (#1297). */
  status: 'loading' | 'ready' | 'error';
  record: SessionEmployeeRecord | null;
  retry: () => void;
};

/**
 * Hook para AppNavigator/PostLoginHub: registro del empleado (isEmployee + isActive).
 * Antes devolvía `SessionEmployeeRecord | null` y un throw de `awaitSessionBootstrap`
 * dejaba `null` para siempre → spinner infinito sin escape en el hub (#1297).
 * Ahora distingue loading/ready/error y expone `retry`.
 * Se re-lee cuando Slice 2 detecta cambio de permissions_updated_at / revoke.
 */
export function useSessionEmployeeRecord(): SessionEmployeeRecordState {
  const session = useAuthStore((s) => s.session);
  const [state, setState] = useState<Omit<SessionEmployeeRecordState, 'retry'>>({
    status: 'loading',
    record: null,
  });
  const [freshnessTick, setFreshnessTick] = useState(0);

  useEffect(() => {
    return subscribeEmployeePermissionsFreshness(() => {
      setFreshnessTick((n) => n + 1);
    });
  }, []);

  const retry = useCallback(() => {
    setState({ status: 'loading', record: null });
    setFreshnessTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!session?.user?.id) {
      setState({ status: 'loading', record: null });
      return;
    }
    const uid = session.user.id;
    let cancelled = false;

    (async () => {
      try {
        await awaitSessionBootstrap(uid);
        const record = await resolveSessionEmployee(uid, freshnessTick > 0 ? { force: true } : undefined);
        if (cancelled) return;
        setState({ status: 'ready', record });
      } catch (e) {
        if (!isDatabaseUnavailableError(e)) {
          console.warn('[useSessionEmployeeRecord]', e);
        }
        if (!cancelled) setState({ status: 'error', record: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, freshnessTick]);

  return { ...state, retry };
}
